/**
 * Browser-side Places, speaking the new `Place` class with a legacy fallback.
 *
 * `google.maps.places.PlacesService` — what this app used everywhere — became
 * unavailable to new customers on 1 March 2025, alongside the legacy Places web
 * service. A Maps key created after that date loads the map, geocodes, and
 * draws Street View, and then returns nothing at all from PlacesService, which
 * reads on screen as "the property has no photos" rather than as a
 * configuration fault.
 *
 * So each call tries `Place` first and falls back to `PlacesService` only for a
 * project that still has the legacy API. Which one answered is remembered for
 * the page's lifetime so the fallback is paid once, not per lookup.
 */

import { loadMapsScript, mapsAuthFailed, mapsLibraries } from '@/services/googleMapsLoader'

/**
 * Every callback-style Maps API can simply never call back.
 *
 * When Google rejects the key for this site it reports `InvalidKeyMapError` to
 * the console and then drops the request on the floor — Geocoder,
 * PlacesService and StreetViewService all take the callback and never invoke
 * it. Awaiting one of those without a deadline hangs the caller for good,
 * which for a diagnostic means a button stuck on "Testing…" and no answer at
 * all. Six seconds is well past a healthy round trip.
 */
const CALLBACK_DEADLINE_MS = 6000

function withDeadline<T>(work: Promise<T>, fallback: T): Promise<T> {
  return new Promise<T>((resolve) => {
    const timer = setTimeout(() => resolve(fallback), CALLBACK_DEADLINE_MS)
    void work.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      () => {
        clearTimeout(timer)
        resolve(fallback)
      },
    )
  })
}

export type PlacesGeneration = 'new' | 'legacy' | 'none'

export type BrowserPlacePhoto = {
  src: string
  alt: string
  attribution: string | null
}

let generation: PlacesGeneration | null = null

/** Test seam and key-change reset — the memo must not outlive its key. */
export function resetPlacesGeneration(): void {
  generation = null
}

/** Which Places generation this key answered on, once something has asked. */
export function placesGeneration(): PlacesGeneration | null {
  return generation
}

/** Exactly what the loader hands back — the imported library or the namespace. */
type PlacesNs = NonNullable<Awaited<ReturnType<typeof mapsLibraries>>['places']>

async function places(): Promise<PlacesNs | undefined> {
  const libs = await mapsLibraries()
  return libs.places
}

/**
 * Photos Google holds for a place ID, largest-useful size first.
 *
 * Returns `null` — not an empty list — when neither Places generation is
 * enabled, so the caller can say why rather than showing an empty gallery.
 */
export async function fetchPlacePhotos(
  placeId: string,
  fallbackAlt: string,
  limit = 8,
): Promise<BrowserPlacePhoto[] | null> {
  const ns = await places()
  if (!ns) return null

  if (generation !== 'legacy' && ns.Place) {
    try {
      const place = new ns.Place({ id: placeId })
      await place.fetchFields({ fields: ['displayName', 'photos'] })
      generation = 'new'
      const name = place.displayName || fallbackAlt
      return (place.photos ?? []).slice(0, limit).flatMap((photo) => {
        const src = photo.getURI({ maxWidth: 1600 })
        if (!src) return []
        return [
          {
            src,
            alt: name,
            // Google's terms require the contributor to be shown beside the
            // photo wherever it appears.
            attribution: photo.authorAttributions?.[0]?.displayName ?? null,
          },
        ]
      })
    } catch (error) {
      if (!isServiceDisabled(error)) throw error
    }
  }

  const legacy = await legacyDetailsPhotos(ns, placeId, fallbackAlt, limit)
  if (legacy !== null) generation = 'legacy'
  else generation = 'none'
  return legacy
}

/**
 * How many places of the given types stand within `radius` metres.
 *
 * `null` means the question could not be asked — never zero, which would read
 * as "there is no transit here".
 */
export async function countNearby(
  center: google.maps.LatLng,
  includedTypes: string[],
  radius: number,
): Promise<number | null> {
  const ns = await places()
  if (!ns) return null

  if (generation !== 'legacy' && ns.Place?.searchNearby) {
    try {
      const { places: found } = await ns.Place.searchNearby({
        fields: ['displayName'],
        locationRestriction: { center, radius },
        includedTypes,
        maxResultCount: 20,
      })
      generation = 'new'
      return found.length
    } catch (error) {
      if (!isServiceDisabled(error)) throw error
    }
  }

  const legacy = await legacyNearbyCount(ns, center, includedTypes[0], radius)
  if (legacy !== null) generation = 'legacy'
  return legacy
}

/**
 * A refusal that means "ask the other generation", not "this key is broken".
 *
 * The new API answers a disabled service with a 403 naming it; a key that is
 * merely referrer-blocked or invalid must not be retried on legacy, because
 * legacy will fail the same way and the second failure hides the first.
 */
function isServiceDisabled(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  const lowered = message.toLowerCase()
  return (
    lowered.includes('has not been used in project') ||
    lowered.includes('is not enabled') ||
    lowered.includes('service_disabled') ||
    lowered.includes('api_key_service_blocked') ||
    lowered.includes('not authorized')
  )
}

export type BrowserMapsCheck = {
  api: string
  ok: boolean
  message: string
  remedy: string | null
}

/**
 * Test the browser key against the APIs the page itself calls.
 *
 * The server cannot do this: a correctly referrer-restricted browser key is
 * refused by every REST endpoint, so a server-side probe can only say "it is
 * restricted" and not "Maps JS and Places are enabled on it". Only the browser
 * can answer that, and until it does the dossier map is the sole test — which
 * is a poor place to discover a missing API.
 */
export async function probeBrowserMaps(apiKey: string): Promise<BrowserMapsCheck[]> {
  const checks: BrowserMapsCheck[] = []
  try {
    await loadMapsScript(apiKey)
  } catch (error) {
    return [
      {
        api: 'Maps JavaScript API',
        ok: false,
        message: error instanceof Error ? error.message : 'Google Maps failed to load.',
        remedy:
          'Enable “Maps JavaScript API” in Google Cloud and add this site to the key\'s HTTP-referrer list.',
      },
    ]
  }
  checks.push({
    api: 'Maps JavaScript API',
    ok: true,
    message: 'Loaded on this page.',
    remedy: null,
  })

  const libs = await mapsLibraries()

  const geocoded = await withDeadline(
    new Promise<boolean>((resolve) => {
      if (!libs.Geocoder) return resolve(false)
      void new libs.Geocoder().geocode({ address: PROBE_ADDRESS }, (results, status) => {
        resolve(status === 'OK' && Boolean(results?.[0]))
      })
    }),
    false,
  )

  // Checked after the first call, because Google reports the rejection
  // asynchronously — the script loads and only then is the key refused.
  if (mapsAuthFailed()) {
    return [
      ...checks.slice(0, 1),
      {
        api: 'Key authorization',
        ok: false,
        message:
          'Google rejected this key for this site, so Maps JS calls return nothing. ' +
          'The browser console names the exact error.',
        remedy:
          "Check the key is valid, has billing enabled, and lists this site's origin under HTTP referrers.",
      },
    ]
  }

  checks.push({
    api: 'Geocoding API',
    ok: geocoded,
    message: geocoded
      ? 'Resolved the probe address.'
      : 'No answer for the probe address within 6 seconds.',
    remedy: geocoded ? null : 'Enable “Geocoding API” and allow it on this key.',
  })

  resetPlacesGeneration()
  let placesOk = false
  let placesMessage = 'Neither Places generation answered for this key.'
  try {
    const count = await withDeadline(
      countNearby(new google.maps.LatLng(PROBE_LAT, PROBE_LNG), ['park'], 800),
      null,
    )
    placesOk = count !== null
    if (placesOk) {
      placesMessage =
        placesGeneration() === 'legacy'
          ? 'Answered on the legacy Places API. Enable “Places API (New)” before Google retires it.'
          : 'Answered on Places API (New).'
    }
  } catch (error) {
    placesMessage = error instanceof Error ? error.message : String(error)
  }
  checks.push({
    api: 'Places API (New)',
    ok: placesOk,
    message: placesMessage,
    remedy: placesOk
      ? null
      : 'Enable “Places API (New)” in Google Cloud and allow it on this key. The legacy Places API cannot be enabled on any project after 1 March 2025.',
  })

  return checks
}

const PROBE_ADDRESS = '350 5th Ave, New York, NY 10118'
const PROBE_LAT = 40.7484
const PROBE_LNG = -73.9857

async function legacyDetailsPhotos(
  ns: PlacesNs,
  placeId: string,
  fallbackAlt: string,
  limit: number,
): Promise<BrowserPlacePhoto[] | null> {
  const ctor = ns.PlacesService
  if (!ctor) return null
  const service = new ctor(document.createElement('div'))
  const result = await withDeadline(
    new Promise<google.maps.places.PlaceResult | null>((resolve) => {
      service.getDetails({ placeId, fields: ['name', 'photos'] }, (place, status) => {
        resolve(status === ns.PlacesServiceStatus.OK && place ? place : null)
      })
    }),
    null,
  )
  if (!result) return null
  const name = result.name || fallbackAlt
  return (result.photos ?? []).slice(0, limit).flatMap((photo) => {
    try {
      const src = photo.getUrl({ maxWidth: 1600 })
      return src ? [{ src, alt: name, attribution: photo.html_attributions?.[0] ?? null }] : []
    } catch {
      return []
    }
  })
}

async function legacyNearbyCount(
  ns: PlacesNs,
  center: google.maps.LatLng,
  type: string | undefined,
  radius: number,
): Promise<number | null> {
  const ctor = ns.PlacesService
  if (!ctor || !type) return null
  const service = new ctor(document.createElement('div'))
  return withDeadline(
    new Promise<number | null>((resolve) => {
      service.nearbySearch({ location: center, radius, type }, (results, status) => {
        if (status === ns.PlacesServiceStatus.OK) resolve(results?.length ?? 0)
        else if (status === ns.PlacesServiceStatus.ZERO_RESULTS) resolve(0)
        else resolve(null)
      })
    }),
    null,
  )
}
